import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { enUS, roRO } from '@mui/x-date-pickers/locales';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/ro';
import { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * `en` maps to `en-gb`, not to `en`.
 *
 * dayjs's `en` locale is American: its weeks start on Sunday, so the calendar grid would
 * read S M T W T F S. The clinic's week starts on Monday -- `startOfWeek` in
 * `library/dateTimeHelper` exists to say exactly that, and `MyCalendar` draws its columns
 * that way -- and `en-gb` is the same language with that one fact corrected.
 *
 * Two entries and no fallback path worth writing: `src/i18n.js` strips the region from a
 * detected language, so `resolvedLanguage` is only ever `ro` or `en`.
 */
const DAYJS_LOCALE: Record<string, string> = { ro: 'ro', en: 'en-gb' };

/**
 * The date adapter every picker in the app reads its locale from.
 *
 * A component rather than a bare `<LocalizationProvider>` at the call site, because
 * `index.tsx` is not a component and cannot call `useTranslation` -- and the adapter's
 * locale has to follow the app's language rather than the browser's, which is the whole
 * reason the native inputs were replaced. `useTranslation` needs no `I18nextProvider`
 * here: `src/i18n.js` registers the singleton through `initReactI18next`.
 *
 * The month and weekday names come from dayjs; the *labels* -- "Choose date", the
 * clear and today buttons, every aria-label a screen reader reads -- come from MUI's own
 * translations, which is what `localeText` supplies. Without it those stay English on a
 * Romanian screen.
 */
export const AppLocalizationProvider = ({ children }: { children: ReactNode }): JSX.Element => {
    const { i18n } = useTranslation();
    const language: string = i18n.resolvedLanguage ?? 'ro';

    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={DAYJS_LOCALE[language] ?? 'ro'}
            localeText={
                (language === 'ro' ? roRO : enUS)
                    .components.MuiLocalizationProvider.defaultProps.localeText
            }
        >
            {children}
        </LocalizationProvider>
    );
};
